import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useChurch } from './ChurchContext';
import api from '../api/axios';

export const DEFAULT_CONFIG = {
  // Identité
  siteName: 'RCC',
  logoUrl: '',

  // Couleurs principales
  primaryColor: '#000000',
  sidebarBg: '#000000',
  sidebarText: '#a3a3a3',
  sidebarActive: '#333333',
  accentColor: '#000000',

  // Fond & surfaces
  pageBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderColor: '#e5e5e5',

  // Typographie
  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
  fontSize: '15',

  // Boutons
  btnRadius: '8',
  btnPrimaryBg: '#000000',
  btnPrimaryText: '#ffffff',
  btnSecondaryBg: '#f0f0f0',
  btnSecondaryText: '#000000',
  btnDangerBg: '#fef2f2',
  btnDangerText: '#b91c1c',

  // Cartes
  cardRadius: '14',
  cardShadow: '0 1px 4px rgba(0,0,0,0.08)',

  // Textes
  textPrimary: '#000000',
  textSecondary: '#404040',
  textMuted: '#a3a3a3',
};

function storageKey(churchId) {
  return churchId ? `site_config_${churchId}` : 'site_config_global';
}

// Anciennes couleurs vertes par défaut — si un thème sauvegardé les utilise encore
// telles quelles (jamais personnalisées par l'admin), on les purge pour laisser
// passer les nouveaux défauts noir/blanc plutôt que de figer le vert pour toujours.
const LEGACY_GREEN_VALUES = new Set([
  '#4A7C59', '#2D4A3E', '#5B8C5A', '#A8C4B0', '#F5F0E8', '#FDFBF7', '#E8DFD0',
  '#2C1F14', '#6B5744', '#9C8878', '#F0EAE0', '#3D3028',
  // Ancien gris foncé (première passe noir/blanc) — remplacé par du noir pur
  '#1A1A1A', '#404040', '#525252', '#171717',
]);

function loadConfig(churchId) {
  try {
    const saved = localStorage.getItem(storageKey(churchId));
    if (!saved) return { ...DEFAULT_CONFIG };
    const parsed = JSON.parse(saved);
    const cleaned = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string' && LEGACY_GREEN_VALUES.has(value.toUpperCase())) continue;
      cleaned[key] = value;
    }
    return { ...DEFAULT_CONFIG, ...cleaned };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

function applyTheme(cfg) {
  const root = document.documentElement;
  root.style.setProperty('--primary', cfg.primaryColor);
  root.style.setProperty('--primary-dark', darken(cfg.primaryColor, 15));
  root.style.setProperty('--sidebar-bg', cfg.sidebarBg);
  root.style.setProperty('--sidebar-text', cfg.sidebarText);
  root.style.setProperty('--sidebar-active', cfg.sidebarActive);
  root.style.setProperty('--accent', cfg.accentColor);
  root.style.setProperty('--page-bg', cfg.pageBg);
  root.style.setProperty('--card-bg', cfg.cardBg);
  root.style.setProperty('--border', cfg.borderColor);
  root.style.setProperty('--font-family', cfg.fontFamily);
  root.style.setProperty('--font-size', cfg.fontSize + 'px');
  root.style.setProperty('--btn-radius', cfg.btnRadius + 'px');
  root.style.setProperty('--btn-primary-bg', cfg.btnPrimaryBg);
  root.style.setProperty('--btn-primary-text', cfg.btnPrimaryText);
  root.style.setProperty('--btn-secondary-bg', cfg.btnSecondaryBg);
  root.style.setProperty('--btn-secondary-text', cfg.btnSecondaryText);
  root.style.setProperty('--btn-danger-bg', cfg.btnDangerBg);
  root.style.setProperty('--btn-danger-text', cfg.btnDangerText);
  root.style.setProperty('--card-radius', cfg.cardRadius + 'px');
  root.style.setProperty('--card-shadow', cfg.cardShadow);
  root.style.setProperty('--text-primary', cfg.textPrimary);
  root.style.setProperty('--text-secondary', cfg.textSecondary);
  root.style.setProperty('--text-muted', cfg.textMuted);
  document.body.style.fontFamily = cfg.fontFamily;
  document.body.style.backgroundColor = cfg.pageBg;
}

function applyFavicon(url) {
  if (!url) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;

  // Icône d'écran d'accueil iOS (Safari ne lit pas le manifest pour ça)
  let appleLink = document.querySelector("link[rel='apple-touch-icon']");
  if (!appleLink) {
    appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    document.head.appendChild(appleLink);
  }
  appleLink.href = url;

  // Icône PWA (installation Android/Chrome) : on réécrit le manifest avec ce logo
  let manifestLink = document.querySelector("link[rel='manifest']");
  fetch(manifestLink.href)
    .then(r => r.json())
    .then(manifest => {
      manifest.icons = ['192x192', '512x512'].map(sizes => ({
        src: url,
        sizes,
        type: 'image/png',
        purpose: 'any maskable',
      }));
      // start_url/scope/shortcuts[].url sont relatifs dans le manifest source ;
      // servis via blob:, ils doivent être absolus sinon le navigateur les
      // résout contre l'origine blob: et les rejette ("URL is invalid").
      const abs = (path) => new URL(path, window.location.origin).href;
      if (manifest.start_url) manifest.start_url = abs(manifest.start_url);
      if (manifest.scope) manifest.scope = abs(manifest.scope);
      if (Array.isArray(manifest.shortcuts)) {
        manifest.shortcuts = manifest.shortcuts.map(s => s.url ? { ...s, url: abs(s.url) } : s);
      }
      const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
      manifestLink.href = URL.createObjectURL(blob);
    })
    .catch(() => {});
}

function darken(hex, amount) {
  try {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, (num >> 16) - amount);
    const g = Math.max(0, ((num >> 8) & 0xff) - amount);
    const b = Math.max(0, (num & 0xff) - amount);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  } catch { return hex; }
}

const SiteConfigContext = createContext(null);

export function SiteConfigProvider({ children }) {
  const { activeChurchId } = useChurch();
  const prevChurchId = useRef(activeChurchId);

  const [config, setConfig] = useState(() => {
    const cfg = loadConfig(activeChurchId);
    applyTheme(cfg);
    return cfg;
  });

  const fetchAndMerge = useCallback((churchId) => {
    const params = churchId ? { church_id: churchId } : {};
    api.get('/notifications/church-settings/', { params })
      .then(r => {
        const serverName = r.data.church_name;
        const serverTheme = r.data.theme_config || {};
        // En mode global (churchId=null) : ne jamais copier le logo d'une église
        const serverLogo = churchId ? (r.data.logo || '') : '';

        setConfig(prev => {
          const next = {
            ...prev,
            ...(Object.keys(serverTheme).length ? serverTheme : {}),
            ...(serverName ? { siteName: serverName } : {}),
            logoUrl: serverLogo,
          };
          localStorage.setItem(storageKey(churchId), JSON.stringify(next));
          applyTheme(next);
          return next;
        });
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // Au montage
  useEffect(() => {
    fetchAndMerge(activeChurchId);
    // Favicon : toujours issu de la config globale, indépendamment de l'église active
    api.get('/notifications/church-settings/')
      .then(r => applyFavicon(r.data.favicon))
      .catch(() => {});
  }, []); // eslint-disable-line

  // Quand l'église change → recharger complètement
  useEffect(() => {
    if (prevChurchId.current === activeChurchId) return;
    prevChurchId.current = activeChurchId;
    // Repartir des defaults pour éviter que le logo/nom de l'ancienne église reste
    const cfg = activeChurchId === null
      ? { ...DEFAULT_CONFIG, siteName: 'RCC', logoUrl: '' }
      : loadConfig(activeChurchId);
    setConfig(cfg);
    applyTheme(cfg);
    fetchAndMerge(activeChurchId);
  }, [activeChurchId, fetchAndMerge]);

  useEffect(() => { applyTheme(config); }, [config]);

  const updateConfig = useCallback((updates) => {
    setConfig(prev => {
      const next = { ...prev, ...updates };
      localStorage.setItem(storageKey(activeChurchId), JSON.stringify(next));
      return next;
    });
  }, [activeChurchId]);

  const resetConfig = useCallback(() => {
    localStorage.removeItem(storageKey(activeChurchId));
    const cfg = { ...DEFAULT_CONFIG };
    setConfig(cfg);
    applyTheme(cfg);
  }, [activeChurchId]);

  return (
    <SiteConfigContext.Provider value={{ config, updateConfig, resetConfig }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
